import { Autocomplete, Chip, FormControl, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import { useMaterialUIController } from "context";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "rsuite";
import moment from "moment-timezone";
import Statistics from "./SubscriptionStatistics";
import CircularIndeterminate from "components/MDLoading";
import { isEmpty } from "lodash";
import { amountToShow } from "global/functions";
import * as XLSX from "xlsx";
// import DownloadIcon from "@mui/icons-material/Download";
import { getSubscriptionReport } from "store/slice/reports/reportSlice";
import NoDataOverlay from "components/Common/NoDataOverlay";
import MDSnackbar from "components/MDSnackbar";

function SubscriptionReports() {
  const [controller] = useMaterialUIController();
  const dispatch = useDispatch();
  const { sidenavColor } = controller;
  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  const placeId = selectedPlace?._id;
  const tz = selectedPlace?.timeZoneId || "America/Los_Angeles";

  const [statistics, setStatistics] = useState({});
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const loading = useSelector((state) => state.report?.loading);

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });
  const currentDate = moment().toDate();

  const [filterOptions, setFilterOptions] = useState({
    tz,
    status: "All",
    placeId,
    startDate: moment(currentDate),
    endDate: moment(currentDate),
  });

  useEffect(() => {
    setFilterOptions((prev) => ({
      ...prev,
      placeId: selectedPlace?._id || null,
      tz: selectedPlace?.timeZoneId || "America/Los_Angeles",
    }));
  }, [selectedPlace]);

  const handleStartDateChange = (value) => {
    setFilterOptions((prev) => ({
      ...prev,
      startDate: value ? moment(value) : null,
    }));
  };

  const handleEndDateChange = (value) => {
    setFilterOptions((prev) => ({
      ...prev,
      endDate: value ? moment(value) : null,
    }));
  };

  const handleGetReport = async () => {
    try {
      const payload = {
        ...filterOptions,
        status: filterOptions.status.toLowerCase(),
        startDate: moment(filterOptions.startDate)
          .format("YYYY-MM-DD")
          .toString(),
        endDate: moment(filterOptions.endDate).format("YYYY-MM-DD").toString(),
        page: 1,
        limit: 0,
        statsOnly: true,
      };
      const res = await dispatch(getSubscriptionReport(payload)).unwrap();
      console.log("res ===>", res);
      if (res.success) {
        setStatistics(res.subscriptionStatistics);
        setTotalSubscriptions(res.totalSubscriptions || 0);
      }
    } catch (error) {
      console.log("error --->", error.message);
    }
  };

  const downloadReport = async () => {
    try {
      if (totalSubscriptions === 0) return;
      const pageSize = 1000;
      const allData = [];
      let page = 1;
      setDownloading(true);
      setDownloadProgress(0);
      while (true) {
        const pagePayload = {
          ...filterOptions,
          status: filterOptions.status.toLowerCase(),
          startDate: moment(filterOptions.startDate)
            .format("YYYY-MM-DD")
            .toString(),
          endDate: moment(filterOptions.endDate)
            .format("YYYY-MM-DD")
            .toString(),
          page,
          limit: pageSize,
          statsOnly: false,
        };
        let pageRes;
        try {
          pageRes = await dispatch(getSubscriptionReport(pagePayload)).unwrap();
        } catch (e) {
          throw new Error(
            e?.message || "Failed to fetch a page of subscriptions",
          );
        }
        const dataChunk = pageRes?.subscriptions || [];
        allData.push(...dataChunk);
        const fetched = Math.min(allData.length, totalSubscriptions);
        const pct = Math.round((fetched / totalSubscriptions) * 100);
        setDownloadProgress(pct);
        if (dataChunk.length < pageSize) break;
        page += 1;
      }
      const data = allData;
      const columnMapping = {
        "Subscription ID": "subscriptionNumber",
        "Start Date": "startDate",
        "End Date": "endDate",
        "License Plate": "licensePlate",
        Email: "customerId.email",
        Mobile: "customerId.mobile",
        Name: "customerId.fullName",
        "Subscription Type": "isMonthly",
        "Auto Renew": "isAutoRenew",
        "Payment Method": "paymentMethodType",
        "Total Amount": "totalAmount",
        "Base Rate": "baseRate",
        Tax: "tax",
        "Service Fee": "serviceFee",
        "Merchant Fee": "paymentGatewayFee",
        "Dock Revenue": "dockRevenue",
        "Owner Payout": "ownerPayout",
        "Pro-Rate TotalAmount": "firstMonthTotalAmount",
        "Pro-Rate BaseRate": "firstMonthBaseRate",
        "Pro-Rate Tax": "firstMonthTax",
        "Pro-Rate ServiceFee": "firstMonthServiceFee",
        "Pro-Rate MerchentFee": "firstMonthPaymentGatewayFee",
        "Pro-Rate Dock Revenue": "firstMonthDockRevenue",
        "Pro-Rate Owner Payout": "firstMonthTotalAmount",
      };

      const filteredData = data.map((item) => {
        const filteredItem = {};
        Object.keys(columnMapping).forEach((columnName) => {
          const key = columnMapping[columnName];
          let value = item[key];
          if (key === "customerId.fullName") {
            const firstName =
              item.customerId && item.customerId.firstName
                ? item.customerId.firstName
                : "";
            const lastName =
              item.customerId && item.customerId.lastName
                ? item.customerId.lastName
                : "";
            value = `${firstName} ${lastName}`.trim();
          } else if (key === "startDate" || key === "endDate") {
         value= value  ? moment.utc(value).tz(tz).format("YYYY-MM-DD HH:mm:ss") :"";
          } else if (key === "licensePlate") {
            value = value
              ? value.map((plate) => plate.licensePlateNumber).join(", ")
              : "";
          } else if (key === "customerId.email") {
            value =
              item["customerId"] && item["customerId"]["email"]
                ? item["customerId"]["email"]
                : "";
          } else if (key === "customerId.mobile") {
            value =
              item["customerId"] && item["customerId"]["mobile"]
                ? item["customerId"]["mobile"]
                : item["customerId"] && item["customerId"]["secondaryMobile"]
                  ? item["customerId"]["secondaryMobile"]
                  : "";
          } else if (
            [
              "baseRate",
              "tax",
              "serviceFee",
              "paymentGatewayFee",
              "dockRevenue",
              "ownerPayout",
              "totalAmount",
              "firstMonthBaseRate",
              "firstMonthServiceFee",
              "firstMonthTax",
              "firstMonthTotalAmount",
              "firstMonthOwnerPayout",
              "firstMonthDockRevenue",
              "firstMonthApplicationFee",
              "firstMonthPaymentGatewayFee",
            ].includes(key)
          ) {
            value = value ? `$${amountToShow(value)}` : `$0`;
          } else if (key === "paymentMethodType") {
            value =
              value === "card"
                ? "Credit Card"
                : value === "ACH"
                  ? "ACH"
                  : value;
          } else if (key === "isMonthly") {
            value = value === true ? "Monthly" : "Custom";
          } else if (key === "isAutoRenew") {
            value = value === true ? "Yes" : "No";
          }
          filteredItem[columnName] = value;
        });
        return filteredItem;
      });

      const ws = XLSX.utils.json_to_sheet(filteredData);

      const columnWidths = Object.keys(columnMapping).map((columnName) => {
        switch (columnName) {
          case "Subscription ID":
            return { wch: 15 };
          case "Start Date":
          case "End Date":
            return { wch: 15 };
          case "Base Rate":
          case "Tax":
          case "Service Fee":
          case "Total Amount":
            return { wch: 10 };
          case "License Plate":
            return { wch: 30 };
          default:
            return { wch: 25 };
        }
      });
      ws["!cols"] = columnWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const safeFileName = `subscription-report_${moment().format(
        "YYYY-MM-DD_HH-mm",
      )}.xlsx`;
      XLSX.writeFile(wb, safeFileName);
      setNotification({
        ...notification,
        color: "success",
        title: "Download ready",
        content: safeFileName,
        icon: "check",
        show: true,
      });
      setDownloading(false);
      setDownloadProgress(0);
    } catch (e) {
      console.error("download error", e);
      setDownloading(false);
      setDownloadProgress(0);
      setNotification({
        ...notification,
        color: "error",
        title: "Download failed",
        content: e?.message || "Unable to generate subscription report",
        icon: "error",
        show: true,
      });
    }
  };

  return (
    <>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            <PlacePicker />
          </Grid>
          <Grid item xs={12} md={2}>
            <MDBox display="flex" justifyContent="flex-start" gap={1}>
              <Tooltip title="Get report" arrow>
                <MDButton
                  size="small"
                  color={sidenavColor}
                  disabled={
                    filterOptions.startDate && filterOptions.endDate
                      ? false
                      : true
                  }
                  onClick={handleGetReport}
                >
                  Go
                </MDButton>
              </Tooltip>
              <Tooltip title="Export this report" arrow>
                <MDButton
                  color={sidenavColor}
                  disabled={totalSubscriptions === 0 || downloading}
                  onClick={downloadReport}
                  size="small"
                  // endIcon={<DownloadIcon />}
                >
                  {downloading ? `${downloadProgress}%` : "Download"}
                </MDButton>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <DatePicker
                id="startDate"
                placeholder="Start Date"
                size="md"
                format="MM/dd/yyyy"
                ranges={[]}
                value={
                  filterOptions.startDate
                    ? moment(filterOptions.startDate).toDate()
                    : null
                }
                onChange={handleStartDateChange}
                onClean={() => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    startDate: null,
                  }));
                  setStatistics({});
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <DatePicker
                id="endDate"
                placeholder="End Date"
                size="md"
                format="MM/dd/yyyy"
                ranges={[]}
                value={
                  filterOptions.endDate
                    ? moment(filterOptions.endDate).toDate()
                    : null
                }
                onChange={handleEndDateChange}
                onClean={() => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    endDate: null,
                  }));
                  setStatistics({});
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <Autocomplete
                disableClearable
                name="type"
                value={filterOptions.status}
                options={[
                  "All",
                  "Active",
                  "Failed",
                  "Refunded",
                  "Payment Pending",
                ]}
                onChange={(event, newValue) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    status: newValue,
                  }));
                }}
                freeSolo
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="contained"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MDInput
                    label="Subscription Type"
                    fullWidth={true}
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </MDBox>
      {!isEmpty(statistics) ? (
        <>
          <MDBox pt={2}>
            <Statistics data={statistics} />
          </MDBox>
        </>
      ) : (
        <MDBox pt={4}>
          <NoDataOverlay />
        </MDBox>
      )}

      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..."
        open={loading}
      />
    </>
  );
}

export default SubscriptionReports;
